import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { useCurrency } from '@/shared/hooks/useCurrency';
import {
  useFinancialYears,
  useFinancialYearSnapshot,
} from '../hooks/useFinancialYears';
import {
  FINANCIAL_YEAR_STATUS_LABEL,
  FinancialYearStatus,
  type AccountBalanceDto,
  type ClosingSnapshotDto,
} from '../services/financialYearsApi';
import { ArrowLeft, GitCompare } from 'lucide-react';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });

interface ComparisonRow {
  accountCode: string;
  accountName: string;
  accountType: string;
  aDebit: number;
  aCredit: number;
  bDebit: number;
  bCredit: number;
}

const buildComparison = (a: ClosingSnapshotDto | undefined, b: ClosingSnapshotDto | undefined): ComparisonRow[] => {
  const map = new Map<string, ComparisonRow>();
  const upsert = (acc: AccountBalanceDto, side: 'a' | 'b') => {
    const key = acc.accountCode;
    const existing = map.get(key);
    if (existing) {
      if (side === 'a') {
        existing.aDebit = acc.debitBalance;
        existing.aCredit = acc.creditBalance;
      } else {
        existing.bDebit = acc.debitBalance;
        existing.bCredit = acc.creditBalance;
      }
    } else {
      map.set(key, {
        accountCode: acc.accountCode,
        accountName: acc.accountName,
        accountType: acc.accountType,
        aDebit: side === 'a' ? acc.debitBalance : 0,
        aCredit: side === 'a' ? acc.creditBalance : 0,
        bDebit: side === 'b' ? acc.debitBalance : 0,
        bCredit: side === 'b' ? acc.creditBalance : 0,
      });
    }
  };

  a?.trialBalance.forEach((acc) => upsert(acc, 'a'));
  b?.trialBalance.forEach((acc) => upsert(acc, 'b'));

  return [...map.values()].sort((x, y) => x.accountCode.localeCompare(y.accountCode));
};

export const FinancialYearComparePage = () => {
  const [params, setParams] = useSearchParams();
  const aId = params.get('a') ?? '';
  const bId = params.get('b') ?? '';

  const { data: years } = useFinancialYears();
  const { data: aSnapshot, error: aError, isLoading: aLoading } = useFinancialYearSnapshot(aId || undefined);
  const { data: bSnapshot, error: bError, isLoading: bLoading } = useFinancialYearSnapshot(bId || undefined);
  const { format } = useCurrency();

  const closableYears = useMemo(
    () => (years ?? []).filter((y) => y.status === FinancialYearStatus.Closing || y.status === FinancialYearStatus.Closed || y.status === FinancialYearStatus.Locked),
    [years],
  );

  const comparison = useMemo(() => buildComparison(aSnapshot, bSnapshot), [aSnapshot, bSnapshot]);

  const setSelection = (key: 'a' | 'b', value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/accounting/financial-years" className="hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="size-4" /> Financial years
        </Link>
        <span>/</span>
        <span>Compare</span>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <GitCompare className="size-6" /> Compare financial years
        </h1>
        <p className="text-muted-foreground mt-1">
          Pick any two closed (or locked) financial years to render their trial balances side by side.
          Years that have not been closed yet have no snapshot to compare from.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pick years</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <YearPicker
            label="Year A"
            valueId={aId}
            options={closableYears}
            onChange={(v) => setSelection('a', v)}
          />
          <YearPicker
            label="Year B"
            valueId={bId}
            options={closableYears}
            onChange={(v) => setSelection('b', v)}
          />
        </CardContent>
      </Card>

      {(aLoading || bLoading) && <LoadingAnimation />}

      {(aError || bError) && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-sm text-red-700">
            One of the selected years has no closing snapshot — only Closing, Closed, or Locked years can be
            compared. Closed the year first via its detail page.
          </CardContent>
        </Card>
      )}

      {aSnapshot && bSnapshot && (
        <ComparisonReport a={aSnapshot} b={bSnapshot} rows={comparison} format={format} />
      )}

      {(aSnapshot && !bSnapshot && !bLoading && bId) || (bSnapshot && !aSnapshot && !aLoading && aId) ? (
        <p className="text-sm text-muted-foreground">Pick a second year to render the comparison.</p>
      ) : null}
    </div>
  );
};

const YearPicker = ({
  label,
  valueId,
  options,
  onChange,
}: {
  label: string;
  valueId: string;
  options: ReturnType<typeof useFinancialYears>['data'] extends infer T ? (T extends Array<infer U> ? U[] : never) : never;
  onChange: (id: string) => void;
}) => (
  <div>
    <label className="text-sm font-medium mb-1 block">{label}</label>
    <Select value={valueId || undefined} onValueChange={(v) => onChange(v)}>
      <SelectTrigger>
        <SelectValue placeholder="Select a closed financial year" />
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">No closed years yet.</div>
        )}
        {options.map((y) => (
          <SelectItem key={y.id} value={y.id}>
            {y.label} ({FINANCIAL_YEAR_STATUS_LABEL[y.status]})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const ComparisonReport = ({
  a,
  b,
  rows,
  format,
}: {
  a: ClosingSnapshotDto;
  b: ClosingSnapshotDto;
  rows: ComparisonRow[];
  format: (n: number) => string;
}) => (
  <Card>
    <CardHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <div>
          <CardTitle className="text-base">{a.label}</CardTitle>
          <p className="text-muted-foreground">
            {formatDate(a.periodStart)} → {formatDate(a.periodEnd)}
          </p>
        </div>
        <div className="text-center text-muted-foreground self-end pb-1">vs</div>
        <div className="text-right">
          <CardTitle className="text-base">{b.label}</CardTitle>
          <p className="text-muted-foreground">
            {formatDate(b.periodStart)} → {formatDate(b.periodEnd)}
          </p>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <Stat label="Total income" valueA={a.totalIncome} valueB={b.totalIncome} format={format} />
        <Stat label="Total expense" valueA={a.totalExpense} valueB={b.totalExpense} format={format} />
        <Stat label="Net result" valueA={a.netResult} valueB={b.netResult} format={format} highlight />
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Code</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="w-28">Type</TableHead>
              <TableHead className="text-right">{a.label} balance</TableHead>
              <TableHead className="text-right">{b.label} balance</TableHead>
              <TableHead className="text-right">Δ (B − A)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const aNet = row.aDebit - row.aCredit;
              const bNet = row.bDebit - row.bCredit;
              const diff = bNet - aNet;
              return (
                <TableRow key={row.accountCode}>
                  <TableCell className="font-mono text-xs">{row.accountCode}</TableCell>
                  <TableCell>{row.accountName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.accountType}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{format(aNet)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{format(bNet)}</TableCell>
                  <TableCell className={`text-right font-mono text-sm ${diff !== 0 ? 'font-semibold' : 'text-muted-foreground'}`}>
                    {format(diff)}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  No accounts in either snapshot.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
);

const Stat = ({
  label,
  valueA,
  valueB,
  format,
  highlight,
}: {
  label: string;
  valueA: number;
  valueB: number;
  format: (n: number) => string;
  highlight?: boolean;
}) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
      <div className={`text-base ${highlight ? 'font-semibold' : ''}`}>{format(valueA)}</div>
      <div className="text-center text-xs text-muted-foreground self-center">→</div>
      <div className={`text-right text-base ${highlight ? 'font-semibold text-blue-700' : ''}`}>{format(valueB)}</div>
    </div>
  </div>
);
